import { Api } from '../helpers';
import { lifelineEndPoints } from '../end-points';

const copilotModel = {
  getMenu(data) {
    const body = data;
    const path = lifelineEndPoints.GET_COPILOT_MENU;
    return Api.post({ path }, { body });
  },
  // `sessionId` must be sent again, unchanged, throughout the rest of the
  // flow - it's part of the key the backend uses to find this raise-issue
  // session's record. Response now carries `selected_items`/`options`
  // (the configured complaint-type issues for the selection's L0
  // category) synchronously in this same call, resolved from an
  // admin-configured policy lookup, not a polled Agent Platform session -
  // there is no more separate `identify_complaint` step to call or poll.
  confirmItems(data, sessionId) {
    const body = data;
    const path = lifelineEndPoints.CONFIRM_COPILOT_ITEMS;
    const queryParams = sessionId ? { session_id: sessionId } : {};
    return Api.post({ path, queryParams }, { body });
  },
  // `sessionId` kept the same as `confirmItems` - response is now the same
  // synchronous `{status, data, error}` shape `verify_complaint` used to
  // reach only after triggering and polling an Agent Platform session:
  // the matched issue's configured verification steps come back directly
  // in this call, resolved from policy, not polled - there is no more
  // separate `verify_complaint` step to call or poll.
  confirmComplaint(data, sessionId) {
    const body = data;
    const path = lifelineEndPoints.CONFIRM_COPILOT_COMPLAINT;
    const queryParams = sessionId ? { session_id: sessionId } : {};
    return Api.post({ path, queryParams }, { body });
  },
  // `sessionId` kept the same as the rest of the flow, as a query param -
  // the backend reads it via `ctx.DefaultQuery`, same as `confirmComplaint`/
  // `confirmItems`/`identifyComplaint`; it's not part of the JSON body.
  submitVerification(data, sessionId) {
    const body = data;
    const path = lifelineEndPoints.SUBMIT_COPILOT_VERIFICATION;
    const queryParams = sessionId ? { session_id: sessionId } : {};
    return Api.post({ path, queryParams }, { body });
  },
  // Poll this one - fired as soon as step 4 opens (no separate "confirm"
  // call first; `submitVerification` already did that job for this stage).
  // Same `{status, data, error}` contract as identify_complaint/
  // verify_complaint. `sessionId` as a query param, matching every other
  // session-correlated copilot endpoint (unconfirmed for this one
  // specifically, but consistent with the established backend pattern).
  evaluateResolution(orderId, sessionId) {
    const path = lifelineEndPoints.EVALUATE_COPILOT_RESOLUTION;
    const queryParams = sessionId ? { session_id: sessionId } : {};
    return Api.post({ path, queryParams }, { body: { order_id: orderId } });
  },
  // Poll this one - same `{status, data, error}` contract as
  // identify_complaint/verify_complaint/evaluate_resolution. Only has
  // something to report once `submitVerification` triggers it server-side
  // with `can_verify: false` - see CopilotView.js. `data.options[0]` is the
  // single drafted customer-facing message (`slug: "customer_response"`),
  // not a list of choices like the other steps' options.
  //
  // This endpoint is generic (`recommended_response`, not
  // `cannot_verify_response`) so it can be reused for other drafted-message
  // scenarios later - `type` selects which one; only "cannot_verify" exists
  // today, so this function hardcodes it rather than taking a param nothing
  // can pass yet.
  cannotVerifyResponse(orderId, sessionId) {
    const path = lifelineEndPoints.RECOMMENDED_COPILOT_RESPONSE;
    const queryParams = { order_id: orderId, type: 'cannot_verify' };
    if (sessionId) queryParams.session_id = sessionId;
    return Api.get({ path, queryParams });
  },
  // `sessionId` as a query param, same as the rest of the flow.
  resolve(data, sessionId) {
    const body = data;
    const path = lifelineEndPoints.RESOLVE_COPILOT;
    const queryParams = sessionId ? { session_id: sessionId } : {};
    return Api.post({ path, queryParams }, { body });
  },
};

export default copilotModel;
